import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { apiFetch, apiJson } from '../lib/api'
import { useSession } from '../lib/auth'
import type {
  AvailabilityStatus,
  PlayerPosition,
  ReferenceVerifiedBy,
} from '../types'
import PlayerHero from '../components/profile/PlayerHero'
import AgentCard from '../components/profile/AgentCard'
import PhysicalProfileCard from '../components/profile/PhysicalProfileCard'
import IdentityCard from '../components/profile/IdentityCard'
import ScoutingNarrativeCard from '../components/profile/ScoutingNarrativeCard'
import PlayerStatementCard from '../components/profile/PlayerStatementCard'
import CareerStatsTable, { type CareerStatsRow } from '../components/profile/CareerStatsTable'
import GameLogSection from '../components/profile/GameLogSection'
import CoachReferencesCard from '../components/profile/CoachReferencesCard'
import FilmSection from '../components/profile/FilmSection'
import SocialIcons from '../components/profile/SocialIcons'
import AwardsCard from '../components/profile/AwardsCard'
import ProfileAnalyticsCard, {
  type ProfileViewsSummary,
} from '../components/profile/ProfileAnalyticsCard'
import AvailabilityCard from '../components/profile/AvailabilityCard'
import TargetLeaguesCard from '../components/profile/TargetLeaguesCard'
import OutreachModal from '../components/profile/OutreachModal'
import RepresentModal from '../components/profile/RepresentModal'
import PtsBySeasonChart from '../components/profile/PtsBySeasonChart'
import Avatar from '../components/Avatar'
import { ProfileSkeleton } from '../components/Skeleton'

interface AgentDashboardLite {
  agent: { id: string }
}

interface SimilarPlayer {
  id: string
  slug: string
  name: string
  photo_url: string | null
  position: PlayerPosition | null
  nationality: string | null
  is_verified: boolean
}

interface SimilarResponse {
  results: SimilarPlayer[]
}

interface PublicProfile {
  player: {
    id: string
    slug: string
    name: string
    jersey_number: number | null
    position: PlayerPosition | null
    nationality: string | null
    university: string | null
    dob: string | null
    availability_status: AvailabilityStatus
    contract_end: string | null
    contract_length_sought: string | null
    regional_preferences: string[]
    scouting_narrative: string | null
    photo_url: string | null
    is_verified: boolean
    // M7-socials (2026-06-07) — three nullable URLs. SocialIcons row
    // self-hides when all are null.
    instagram_url: string | null
    youtube_url: string | null
    facebook_url: string | null
    // Tier-1 content batch — first-person statement + manual 3x3 tag.
    statement: string | null
    is_3x3_active: boolean
    // Tier 5 #1 — gates the Game Log section (harvest coverage is keyed
    // on the API-Sports identity; profiles without one never mount it).
    // The API has served this field publicly since Phase A1.
    api_sports_player_id: number | null
    // Report 3.4 — "Last updated" line on the profile.
    updated_at: string
  }
  // M1 (PII) — public /api/profile no longer returns agent email/whatsapp.
  // Identity fields only; contact routes via the Express-interest flow.
  agent: {
    id: string
    slug: string | null
    name: string
    is_fiba_licensed: boolean
    fiba_license_number: string | null
    photo_url: string | null
    agency: { id: string; name: string; country: string | null } | null
  } | null
  references: Array<{
    id: string
    coach_id: string | null
    coach_name: string
    // Migration 0017 — /coaches/:slug link target when the coach has a row.
    coach_slug: string | null
    // Migration 0018 — server-shaped Pro-gated contact (values for Pro
    // coaches, locked flag for everyone else, neither when unset).
    coach_contact?: { email: string | null; whatsapp: string | null }
    contact_locked?: boolean
    team: string | null
    season: string | null
    star_rating: number | null
    quote: string | null
    verified_by: ReferenceVerifiedBy
    created_at: string
  }>
  // PR1 wingspan UX — sidebar card data. See /api/profile/:slug.
  measurements: {
    height_cm: number | null
    weight_kg: number | null
    wingspan_cm: number | null
    standing_reach_cm: number | null
    max_vert_cm: number | null
    measured_at: string | null
    measured_by: string | null
  } | null
  eligibility: Array<{
    passport_country: string
    visa_status: string | null
    valid_until: string | null
  }>
  // Tier-1 content batch — plain string lists post-migration-0016
  // (languages lost its proficiency dimension; passports is new).
  languages: string[]
  passports: string[]
  awards_count: number
  career_stats: CareerStatsRow[]
  // Cleanup PR — present only when caller is authenticated; absent for anon.
  is_saved_by_viewer?: boolean
  // M5.1 — outreach gate. Always present (tri-state):
  //   null  → viewer anon or not a coach → no CTA
  //   false → viewer is a coach, no outreach sent yet → CTA enabled
  //   true  → viewer is a coach, outreach already sent → CTA disabled
  outreach_already_sent_by_viewer: boolean | null
  // Claim→Confirm — representation-request gate (replaces can_claim_by_viewer):
  //   null  → viewer anon or not an agent → no CTA
  //   true  → agent, player unrepresented, no pending request from them → CTA on
  //   false → agent but can't request (represented, or already has a pending) → no CTA
  can_request_representation_by_viewer: boolean | null
  // Claim→Confirm — public, PII-free status for the profile pill.
  representation_status: 'represented' | 'pending' | 'unrepresented'
  // M5.2 — completeness signals for the owner-agent banner.
  profile_completeness: {
    nationality_missing: boolean
    position_missing: boolean
    height_missing: boolean
  }
  // M6.2 — current team + most-prestigious league for that team
  // (resolved server-side by player_count desc). Used by the "Plays
  // for X in Y" line in PlayerHero. null when player has no
  // current_team_id (the ~0.1% unenrichable agent-curated rows).
  current_team: {
    team_name: string
    team_slug: string
    league_name: string
    league_slug: string
  } | null
  // PR3 — below-the-fold main column + remaining sidebar data.
  awards: Array<{
    id: string
    award_name: string
    season: string | null
    source: string | null
    awarded_at: string | null
  }>
  // M7 films-hyperlinks (2026-06-07) — schema simplified to id + url +
  // optional title. See /api/profile/:slug for the matching server shape.
  films: Array<{
    id: string
    url: string
    title: string | null
    created_at: string
  }>
  target_leagues: Array<{
    id: string
    league_name: string
    country: string | null
    tier: 'T1' | 'T2' | 'T3'
    notes: string | null
  }>
  // PR3 — analytics. Optional: only present when server determined the
  // caller is the owner agent. Client also belt-and-suspenders checks
  // viewerAgentId === player.agent.id before rendering.
  profile_views_summary?: ProfileViewsSummary
}

interface MeResponse {
  profile: {
    role: string | null
    is_admin: boolean
  }
}

interface CoachDashboardData {
  coach: { id: string } | null
}

// Report 3.4 — friendly "Last updated" date. updated_at is either the D1
// default "YYYY-MM-DD HH:MM:SS" or an ISO string (from a PUT) — normalize both.
function formatUpdatedAt(raw: string): string {
  if (!raw) return ''
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

interface SearchResult {
  id: string
  slug: string
  name: string
  photo_url: string | null
  position: PlayerPosition | null
  nationality: string | null
  is_verified: boolean
}

interface SearchResponse {
  results: SearchResult[]
  has_more: boolean
}

export default function PlayerProfile() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; data: PublicProfile }
  >({ kind: 'loading' })
  const [viewerCoachId, setViewerCoachId] = useState<string | null>(null)
  const [viewerAgentId, setViewerAgentId] = useState<string | null>(null)
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false)
  // Saved-player toggle. Tracked separately from `state` so the
  // optimistic update (flip → fire → revert on error) doesn't churn the
  // larger profile payload. Hydrated from data.is_saved_by_viewer when
  // the profile lands; stays undefined for anonymous viewers (server
  // omits the field) which hides the Save button.
  const [savedState, setSavedState] = useState<boolean | undefined>(undefined)
  // M5.1 — outreach "already sent" toggle. Hydrated from
  // data.outreach_already_sent_by_viewer when the profile loads;
  // flipped to true locally on a successful POST so the button label
  // updates to "Interest expressed" without a refetch.
  const [outreachSent, setOutreachSent] = useState<boolean | null>(null)
  const [outreachModalOpen, setOutreachModalOpen] = useState(false)
  // Claim→Confirm — represent modal toggle. The
  // data.can_request_representation_by_viewer signal drives CTA visibility;
  // once a request is sent the parent refetches so the CTA reflects the
  // pending state (can_request becomes false — this agent now has a pending
  // request).
  const [representModalOpen, setRepresentModalOpen] = useState(false)
  // H2H compare picker — modal for selecting the second player to compare
  // against. Opens via a "Compare" button below the hero action column.
  const [comparePickerOpen, setComparePickerOpen] = useState(false)
  const [comparePickerQuery, setComparePickerQuery] = useState('')
  const [comparePickerResults, setComparePickerResults] = useState<SearchResult[]>([])
  const [comparePickerLoading, setComparePickerLoading] = useState(false)
  // Admin update-stats operation. Tracks POST sync-stats state so admins
  // see inline feedback + row count on success, friendly errors on failure.
  const [adminSyncing, setAdminSyncing] = useState(false)
  const [adminSyncResult, setAdminSyncResult] = useState<string | null>(null)
  const [adminSyncError, setAdminSyncError] = useState<string | null>(null)
  // M5.2 — bump this to force the profile-load effect to re-run after a
  // claim succeeds (so the page transitions from "discovery" to
  // "owned by me" without a hard refresh). The reload also pulls in
  // viewerAgentId/coachId already via the separate effect on session.
  const [reloadCount, setReloadCount] = useState(0)
  // Profile-view ping dedup. Tracks which player id we've already
  // POSTed for in this React tree's lifetime so React StrictMode's
  // intentional double-mount in dev (and any re-render of this
  // component for the same player) fires the ping exactly once.
  // Navigating to a different player updates the ref → fires for the
  // new id. Server-side dedup (30-min window) is the real backstop;
  // this ref just keeps dev sane.
  const pingedPlayerIdRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setState({ kind: 'error', message: 'no_slug' })
      return
    }
    async function load() {
      try {
        const res = await fetch(`/api/profile/${encodeURIComponent(slug!)}`)
        if (cancelled) return
        if (res.status === 404) {
          setState({ kind: 'error', message: 'Player not found.' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Request failed (${res.status})` })
          return
        }
        const data = (await res.json()) as PublicProfile
        if (cancelled) return
        setState({ kind: 'ready', data })
        setSavedState(data.is_saved_by_viewer)
        setOutreachSent(data.outreach_already_sent_by_viewer)
      } catch (e) {
        if (cancelled) return
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'network_error',
        })
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // reloadCount: bumping triggers a fresh fetch (used after a claim
    // succeeds so the page re-renders as "owned by me").
  }, [slug, reloadCount])

  // If the viewer is signed in, fetch their role-specific entity ID so we
  // can decide which CTAs to show:
  //   - coach → "Write reference" (unless they've already written one)
  //   - representing agent (== player.agent.id) → "Edit player" deep link
  // Both stay hidden for anon viewers + non-matching roles.
  useEffect(() => {
    if (!session?.user) {
      setViewerCoachId(null)
      setViewerAgentId(null)
      setViewerIsAdmin(false)
      return
    }
    let cancelled = false
    async function check() {
      try {
        const me = await apiJson<MeResponse>('/api/me')
        if (cancelled) return
        setViewerIsAdmin(me.profile.is_admin)
        if (me.profile.role === 'coach') {
          const dashboard = await apiJson<CoachDashboardData>('/api/dashboard/coach')
          if (cancelled) return
          setViewerCoachId(dashboard.coach?.id ?? null)
        } else if (me.profile.role === 'agent') {
          const dashboard = await apiJson<AgentDashboardLite>('/api/dashboard/agent')
          if (cancelled) return
          setViewerAgentId(dashboard.agent?.id ?? null)
        }
      } catch {
        if (!cancelled) {
          setViewerCoachId(null)
          setViewerAgentId(null)
          setViewerIsAdmin(false)
        }
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [session])

  // Fire-on-mount profile-view ping. Runs once per real mount per
  // player id, even under React 19 StrictMode's intentional double-
  // mount in dev. Server-side filters apply: self-views (player /
  // owner-agent / admin) are dropped server-side; authenticated
  // viewers get a 30-min dedup window; anonymous views all count.
  //
  // Why client-side and not inside the GET handler: a server-side
  // counter would inflate the metric with crawler hits, OG-preview
  // fetches, and any curl/API consumer. The client mount means only
  // real browser renderings count. See /api/profile-views handler
  // for the matching code comment.
  useEffect(() => {
    if (state.kind !== 'ready') return
    const pid = state.data.player.id
    if (pingedPlayerIdRef.current === pid) return
    pingedPlayerIdRef.current = pid
    void fetch('/api/profile-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ player_id: pid }),
    }).catch(() => {
      // Best-effort tracking. We don't retry; the server's dedup
      // window means a re-mount within 30 min wouldn't double-write
      // anyway.
    })
  }, [state])

  // H2H compare picker search — debounced search as the user types.
  useEffect(() => {
    if (!comparePickerQuery.trim()) {
      setComparePickerResults([])
      return
    }
    setComparePickerLoading(true)
    const timer = setTimeout(() => {
      apiJson<SearchResponse>(`/api/players/search?q=${encodeURIComponent(comparePickerQuery)}&limit=10`)
        .then((data) => {
          setComparePickerResults(data.results)
          setComparePickerLoading(false)
        })
        .catch(() => {
          setComparePickerLoading(false)
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [comparePickerQuery])

  function handleComparePickerSelect(otherSlug: string) {
    if (!slug) return
    navigate(`/compare?a=${encodeURIComponent(slug)}&b=${encodeURIComponent(otherSlug)}`)
  }

  async function toggleSave() {
    if (state.kind !== 'ready' || savedState === undefined) return
    const playerId = state.data.player.id
    const next = !savedState
    // Optimistic: flip locally first so the button feels instant. If the
    // request fails, revert in the catch. Multiple in-flight requests
    // would just settle to the latest server state — POST is idempotent
    // (INSERT OR IGNORE) and DELETE on a missing row is a no-op.
    setSavedState(next)
    try {
      if (next) {
        await apiJson('/api/saved-players', {
          method: 'POST',
          body: JSON.stringify({ player_id: playerId }),
        })
      } else {
        await apiFetch(`/api/saved-players/${encodeURIComponent(playerId)}`, {
          method: 'DELETE',
        }).then((r) => {
          if (!r.ok) throw new Error(`unsave_failed_${r.status}`)
        })
      }
    } catch {
      setSavedState(!next)
    }
  }

  // PROMPT_COACH_SYNC — renamed from handleAdminSync to handleSyncStats.
  // Now available to any coach/agent (not just admin). Handles 429
  // daily_sync_cap with a friendly message.
  async function handleSyncStats() {
    if (state.kind !== 'ready' || adminSyncing) return
    const playerId = state.data.player.id
    setAdminSyncing(true)
    setAdminSyncResult(null)
    setAdminSyncError(null)
    try {
      const res = await apiJson<{ rows_upserted: number }>(`/api/players/${playerId}/sync-stats`, {
        method: 'POST',
      })
      setAdminSyncResult(`${res.rows_upserted} rows upserted`)
      // Trigger profile reload so the updated stats appear immediately.
      setReloadCount((n) => n + 1)
    } catch (e) {
      if (e instanceof Error) {
        if (e.message === 'api_sports_player_id_unset') {
          setAdminSyncError('No API-Sports ID set for this player')
        } else if (e.message === 'daily_sync_cap') {
          setAdminSyncError('Daily update limit reached (5/day) — try again tomorrow')
        } else {
          setAdminSyncError('Sync failed')
        }
      } else {
        setAdminSyncError('Sync failed')
      }
    } finally {
      setAdminSyncing(false)
    }
  }

  if (state.kind === 'loading') {
    // C5 — skeleton instead of a ~1s blank while /api/profile resolves.
    return <ProfileSkeleton />
  }
  if (state.kind === 'error') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-hoop-text-2">{state.message}</p>
      </div>
    )
  }

  const { player, agent, references } = state.data
  const alreadyWroteReference =
    viewerCoachId != null && references.some(ref => ref.coach_id === viewerCoachId)
  const canWriteReference = viewerCoachId != null && !alreadyWroteReference
  const canEditPlayer =
    viewerAgentId != null && agent != null && agent.id === viewerAgentId
  // C7 (QA TB-7) — Express-interest visibility comes straight from the
  // server's tri-state signal carried in the profile payload, NOT from the
  // secondary /api/dashboard/coach fetch. outreach_already_sent_by_viewer is
  // non-null exactly when the viewer is a signed-in coach (the server only
  // populates it for role === 'coach'), so the CTA renders the right state on
  // the first paint. The old viewerCoachId gate lagged behind the profile
  // load, so on reload the button flickered absent before reappearing as
  // "Interest expressed." The disabled "already sent" state is sourced from
  // outreachSent (hydrated from the same field, flipped locally on success).
  const showExpressInterest = state.data.outreach_already_sent_by_viewer !== null
  const expressInterestDisabled = outreachSent === true
  // Claim→Confirm — "Request to represent" CTA visibility trusts
  // can_request_representation_by_viewer alone (the server sets it true only
  // for an authenticated agent on an unrepresented player with no pending
  // request from them). Rendering off the signal avoids the old race where the
  // CTA waited on a second /api/dashboard fetch. The represent POST is authz-
  // protected server-side, so rendering off the signal is safe.
  const showRequestRepresentation =
    state.data.can_request_representation_by_viewer === true
  // actionAccessory cascade — canEditPlayer → showRequestRepresentation →
  // showExpressInterest. Mutually exclusive by role + ownership:
  //   - canEditPlayer requires the viewer to be the owning agent
  //     (agent != null && agent.id === viewerAgentId).
  //   - showRequestRepresentation requires can_request === true (agent +
  //     unrepresented + no pending request from them).
  //   - showExpressInterest requires the coach outreach signal (coach role).
  // A single session is one role (single-shot per spec §7), so the coach-only
  // path can't collide with either agent-only path, and the Edit / Claim split
  // is the agent's mutex on agent_id presence (claimable iff unrepresented).

  const {
    measurements,
    eligibility,
    languages,
    passports,
    awards_count,
    career_stats,
    awards,
    films,
    target_leagues,
    profile_views_summary,
  } = state.data
  const passportCountry = eligibility[0]?.passport_country ?? null
  // SyncBadge attribution — pull from the most-recently-synced
  // `api_basketball` row in career_stats. Agent-submitted rows (and
  // legacy rows with other sources) don't trigger the badge. When no
  // auto-synced row exists, both stay null → SyncBadge renders nothing
  // per its component contract. ISO-8601 `synced_at` lex-compares
  // chronologically, so a plain string sort picks the newest.
  const latestApiSync = career_stats
    .filter((r) => r.source === 'api_basketball')
    .reduce<{ source: string; synced_at: string } | null>(
      (best, row) =>
        best == null || row.synced_at > best.synced_at
          ? { source: row.source, synced_at: row.synced_at }
          : best,
      null,
    )
  const syncSource = latestApiSync?.source ?? null
  const syncedAt = latestApiSync?.synced_at ?? null
  // Belt-and-suspenders gate for the analytics card. The server already
  // omits profile_views_summary for non-owners, so the first half of the
  // && is the authoritative check; the agent-id match is a second wall
  // so a frontend bug can't render leaked data even hypothetically.
  const showAnalytics =
    profile_views_summary !== undefined &&
    viewerAgentId != null &&
    agent != null &&
    agent.id === viewerAgentId
  // PROMPT_COACH_SYNC — any signed-in coach or agent can sync stats.
  // Admin also gets the button (uncapped, as before).
  const canSyncStats =
    viewerIsAdmin ||
    viewerCoachId != null ||
    viewerAgentId != null

  // M5.2 — Complete this profile banner. Shown only to the owner agent
  // and only for fields that have an edit path. M5.3 added the
  // measurements PUT endpoint, so height now joins nationality + position
  // in the rendered list (PlayerEdit gained a MeasurementsForm section
  // that fills it).
  const completenessFields: string[] = []
  if (canEditPlayer) {
    if (state.data.profile_completeness.nationality_missing) {
      completenessFields.push('nationality')
    }
    if (state.data.profile_completeness.position_missing) {
      completenessFields.push('position')
    }
    if (state.data.profile_completeness.height_missing) {
      completenessFields.push('height')
    }
  }
  const showCompletenessBanner = canEditPlayer && completenessFields.length > 0

  // UIR-2 — compute headlineStats from the most recent career_stats season
  // (same rule as UIR-1 discover card: most recent season, pick row with most GP).
  const headlineStats = career_stats.length > 0
    ? (() => {
        // Group by season, pick the row with most GP for each
        const seasonMap = new Map<string, CareerStatsRow>()
        career_stats.forEach(row => {
          const existing = seasonMap.get(row.season)
          if (!existing || (row.games_played ?? 0) > (existing.games_played ?? 0)) {
            seasonMap.set(row.season, row)
          }
        })
        // Sort seasons DESC, take the first
        const seasons = Array.from(seasonMap.keys()).sort().reverse()
        if (seasons.length === 0) return null
        const mostRecent = seasonMap.get(seasons[0])!
        return {
          points_pg: mostRecent.points_pg,
          rebounds_pg: mostRecent.rebounds_pg,
          assists_pg: mostRecent.assists_pg,
          fg_pct: mostRecent.fg_pct,
          games_played: mostRecent.games_played,
          season: mostRecent.season,
        }
      })()
    : null

  return (
    <div className="max-w-[1240px] mx-auto px-6 py-10 space-y-6">
      {showCompletenessBanner && (
        <div className="bg-hoop-amber-bg border border-hoop-amber/30 text-hoop-amber rounded-xl px-5 py-3.5 text-sm flex items-center justify-between gap-3">
          <span>
            <span className="font-medium">Complete this profile</span>
            {' — missing: '}
            <span>{completenessFields.join(', ')}</span>.
          </span>
          <Link
            to={`/dashboard/players/${player.slug}/edit`}
            className="text-hoop-amber font-medium whitespace-nowrap underline hover:no-underline"
          >
            Edit profile →
          </Link>
        </div>
      )}
      <PlayerHero
        player={{
          slug: player.slug,
          name: player.name,
          jersey_number: player.jersey_number,
          position: player.position,
          nationality: player.nationality,
          dob: player.dob,
          availability_status: player.availability_status,
          photo_url: player.photo_url,
          is_verified: player.is_verified,
          is_3x3_active: player.is_3x3_active,
        }}
        measurements={
          measurements
            ? {
                height_cm: measurements.height_cm,
                weight_kg: measurements.weight_kg,
                wingspan_cm: measurements.wingspan_cm,
              }
            : null
        }
        passportCountry={passportCountry}
        awardsCount={awards_count}
        syncSource={syncSource}
        syncedAt={syncedAt}
        isSaved={savedState}
        onToggleSave={savedState !== undefined ? toggleSave : undefined}
        representationStatus={state.data.representation_status}
        currentTeam={state.data.current_team}
        canSyncStats={canSyncStats}
        onSyncStats={canSyncStats ? handleSyncStats : undefined}
        syncStatsInProgress={adminSyncing}
        syncStatsResult={adminSyncResult}
        syncStatsError={adminSyncError}
        headlineStats={headlineStats}
        actionAccessory={
          // Mutually exclusive cascade (see showRequestRepresentation comment):
          //   - owner agent → Edit player link
          //   - agent viewing unrepresented profile → Request to represent CTA
          //   - coach → Express interest CTA
          //   - everyone else → null
          canEditPlayer ? (
            <Link
              to={`/dashboard/players/${player.slug}/edit`}
              className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border border-hoop-border bg-hoop-surface text-hoop-text hover:bg-hoop-bg"
            >
              Edit player →
            </Link>
          ) : showRequestRepresentation ? (
            <button
              type="button"
              onClick={() => setRepresentModalOpen(true)}
              className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90"
            >
              Request to represent
            </button>
          ) : showExpressInterest ? (
            <button
              type="button"
              onClick={() => {
                if (!expressInterestDisabled) setOutreachModalOpen(true)
              }}
              disabled={expressInterestDisabled}
              className={
                expressInterestDisabled
                  ? 'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-hoop-bg text-hoop-text-3 cursor-default'
                  : 'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90'
              }
            >
              {expressInterestDisabled ? 'Interest expressed' : 'Express interest'}
            </button>
          ) : agent != null && !session?.user ? (
            // M1 (PII) — anonymous viewers on a represented player no longer
            // get a raw mailto/WhatsApp. Route them to sign-in, where (as a
            // coach) they can put their interest on record via Express interest
            // (it notifies nobody; contact is off-platform — see outreach.ts).
            <Link
              to="/"
              className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90"
            >
              Express interest
            </Link>
          ) : null
        }
      />
      {showExpressInterest && (
        <OutreachModal
          open={outreachModalOpen}
          playerId={player.id}
          playerName={player.name}
          // M6.2-followup-2 — modal subtitle branches on representation
          // status. Represented players: "we'll route to their agent."
          // Discovery players: "we'll surface to incoming agents."
          playerHasAgent={agent !== null}
          onClose={() => setOutreachModalOpen(false)}
          onSuccess={() => setOutreachSent(true)}
        />
      )}
      {showRequestRepresentation && (
        <RepresentModal
          open={representModalOpen}
          playerId={player.id}
          playerName={player.name}
          onClose={() => setRepresentModalOpen(false)}
          onSuccess={() => {
            // Refetch so the page reflects the new pending state: the status
            // pill flips to "Representation pending" and the CTA disappears
            // (can_request becomes false now this agent has a pending request).
            setReloadCount((n) => n + 1)
          }}
        />
      )}
      {/* H2H compare button — public action available to all viewers. Sits
          below the hero, above the grid, so it's visible but not intrusive. */}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => setComparePickerOpen(true)}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-hoop-border bg-white dark:bg-hoop-surface text-hoop-text hover:bg-hoop-accent-light dark:hover:bg-hoop-accent/10"
        >
          Compare with another player
        </button>
      </div>
      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* UIR-2 left rail: AgentCard · IdentityCard · PtsBySeasonChart ·
         * PhysicalProfileCard · AvailabilityCard · TargetLeaguesCard · socials. */}
        <aside className="space-y-4 order-2 md:order-1">
          {agent && <AgentCard agent={agent} />}
          <IdentityCard
            nationality={player.nationality}
            eligibility={eligibility}
            languages={languages}
            passports={passports}
          />
          <PtsBySeasonChart careerStats={career_stats} />
          <PhysicalProfileCard measurements={measurements} />
          <AvailabilityCard
            contractEnd={player.contract_end}
            contractLengthSought={player.contract_length_sought}
            regions={player.regional_preferences}
          />
          <TargetLeaguesCard leagues={target_leagues} />
          <SocialIcons
            instagramUrl={player.instagram_url}
            youtubeUrl={player.youtube_url}
            facebookUrl={player.facebook_url}
          />
        </aside>
        {/* Main column — PR2 (scouting / career stats / references) +
         * PR3 (films / awards / analytics). Films + Awards hide entirely
         * when empty (main-column rule of thumb). Analytics is gated by
         * server-side omission AND a client-side agent-id match.
         * Background card stays at the bottom — lowest-info, least
         * disruptive to the new rhythm. */}
        <div className="space-y-4 min-w-0 order-1 md:order-2">
          {/* M7 — Film at the top per Kaillum's "first thing a coach
              wants to see" feedback. FilmSection self-hides when the
              array is empty so the rhythm stays intact for un-filmed
              players. */}
          <FilmSection films={films} />
          <ScoutingNarrativeCard narrative={player.scouting_narrative} />
          {/* Tier-1 content batch — the player's own words, adjacent to
              (and visually distinct from) the agent's narrative above.
              Self-hides when unset. */}
          <PlayerStatementCard statement={player.statement} />
          <CareerStatsTable rows={career_stats} syncSource={syncSource} syncedAt={syncedAt} />
          {/* Tier 5 #1 — per-game log. Mounted ONLY for players with an
              api_sports id (harvest coverage candidates); profiles without
              one (agent-created, family test profiles) never render this,
              and covered players with zero rows get the empty state inside. */}
          {player.api_sports_player_id != null && (
            <GameLogSection
              playerSlug={player.slug}
              seasonOptions={[
                ...new Set(
                  career_stats.filter((r) => !r.is_3x3).map((r) => r.season),
                ),
              ].sort().reverse()}
            />
          )}
          <CoachReferencesCard
            references={references}
            playerSlug={player.slug}
            canWriteReference={canWriteReference}
            viewerIsCoach={viewerCoachId != null}
          />
          <AwardsCard awards={awards} />
          {showAnalytics && profile_views_summary && (
            <ProfileAnalyticsCard summary={profile_views_summary} />
          )}
          {player.university && (
            <Card title="Background">
              <Detail label="University" value={player.university} />
            </Card>
          )}
        </div>
      </div>
      <p className="text-xs text-hoop-text-3">
        Last updated {formatUpdatedAt(player.updated_at)}
      </p>
      <SimilarPlayersSection playerId={player.id} />
      {/* H2H compare picker modal */}
      {comparePickerOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold">Choose a player to compare</h2>
              <button
                onClick={() => {
                  setComparePickerOpen(false)
                  setComparePickerQuery('')
                }}
                className="text-hoop-text-2 hover:text-hoop-text"
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              value={comparePickerQuery}
              onChange={(e) => setComparePickerQuery(e.target.value)}
              placeholder="Search by name..."
              className="w-full px-4 py-2 border border-hoop-border rounded bg-white dark:bg-hoop-surface text-hoop-text placeholder:text-hoop-text-3 mb-4"
              autoFocus
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {comparePickerLoading && (
                <div className="text-center py-4 text-hoop-text-2">
                  Loading...
                </div>
              )}
              {!comparePickerLoading && comparePickerResults.length === 0 && comparePickerQuery.trim() && (
                <div className="text-center py-4 text-hoop-text-2">
                  No players found
                </div>
              )}
              {!comparePickerLoading && comparePickerResults.length === 0 && !comparePickerQuery.trim() && (
                <div className="text-center py-4 text-hoop-text-2">
                  Start typing to search
                </div>
              )}
              {comparePickerResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleComparePickerSelect(p.slug)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-hoop-accent-light rounded text-left"
                >
                  <Avatar
                    src={p.photo_url}
                    name={p.name}
                    className="w-12 h-12 rounded-full flex-shrink-0"
                    textClassName="text-sm"
                  />
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-sm text-hoop-text-2">
                      {p.position || '—'} · {p.nationality || '—'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SimilarPlayersSection({ playerId }: { playerId: string }) {
  const [results, setResults] = useState<SimilarPlayer[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    fetch(`/api/players/${encodeURIComponent(playerId)}/similar`)
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          // Soft-fail: section disappears rather than showing an error to a
          // public visitor browsing the profile.
          setResults([])
          return
        }
        const data = (await res.json()) as SimilarResponse
        if (!cancelled) setResults(data.results)
      })
      .catch(() => {
        if (!cancelled) setResults([])
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [playerId])

  if (!loaded || results.length === 0) return null

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-6">
      <h2 className="text-sm font-semibold text-hoop-accent uppercase tracking-wider mb-4">
        Similar players
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {results.map((p) => (
          <SimilarCard key={p.id} player={p} />
        ))}
      </div>
    </section>
  )
}

function SimilarCard({ player }: { player: SimilarPlayer }) {
  return (
    <Link
      to={`/players/${player.slug}`}
      className="group block bg-hoop-bg border border-hoop-border rounded-xl overflow-hidden hover:border-hoop-accent transition-colors"
    >
      <div className="aspect-square bg-hoop-bg overflow-hidden">
        <Avatar
          src={player.photo_url}
          name={player.name}
          className="w-full h-full group-hover:scale-105 transition-transform duration-200"
          textClassName="text-3xl"
        />
      </div>
      <div className="p-2.5">
        <div className="flex items-center justify-between gap-1.5">
          <h3 className="font-medium text-sm text-hoop-text truncate">
            {player.name}
          </h3>
          {player.is_verified && (
            <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-hoop-green-bg text-hoop-green whitespace-nowrap">
              ✓
            </span>
          )}
        </div>
        <div className="text-[11px] text-hoop-text-3 mt-0.5 truncate">
          {[player.position, player.nationality].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
    </Link>
  )
}

function Card({
  title,
  badge,
  children,
}: {
  title: string
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-hoop-accent uppercase tracking-wider">
          {title}
        </h2>
        {badge}
      </div>
      {children}
    </section>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <span className="text-hoop-text-2">{label}:</span>{' '}
      <span className="text-hoop-text">{value}</span>
    </div>
  )
}

